import { useEffect, useMemo, useRef } from 'react';

import { logger } from '@/utils/logger/logger';

import {
  extractMeetingSlug,
  getUserTimezone,
} from '@/container/demo-flow/meeting-booker/meeting-booker-utils';
import { useQueryMeetingAvailability } from '@/hooks/query/useQueryMeetingAvailability';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type MeetingFallbackReason } from '@/models/analytics/features/onboarding-hubspot';
import {
  type OnboardingExperience,
  type UseOnboardingExperienceArgs,
  useOnboardingExperience,
} from '@/utils/onboardingExperience';

export interface ResolvedOnboardingExperience {
  /** The effective experience to render — may differ from the base when a meeting probe falls back. */
  experience: OnboardingExperience;
  /** True while the availability probe is in flight for a `demo` base experience. */
  isProbing: boolean;
  /** Set when the resolver rewrites `demo` → `request-demo`. */
  fallbackReason: MeetingFallbackReason | null;
}

/**
 * Wraps {@link useOnboardingExperience} with a runtime probe of HubSpot meeting
 * availability. When the base experience is `'demo'` and the probe surfaces a
 * problem — zero slots in the current month, an upstream error, or a malformed
 * meeting URL — the effective experience is rewritten to `'request-demo'` so
 * users land on the SDR lead-capture form rather than a dead-end calendar.
 *
 * Reuses {@link useQueryMeetingAvailability}; TanStack Query dedupes by key, so
 * mounting the resolver alongside `<MeetingBooker>` does not double-fetch.
 */
export function useResolvedOnboardingExperience(
  args: UseOnboardingExperienceArgs
): ResolvedOnboardingExperience {
  const baseExperience = useOnboardingExperience(args);

  // `useOnboardingExperience` already memoises the returned object on its
  // inputs, but depend on the primitive fields explicitly so this hook is not
  // coupled to that implementation detail.
  const meetingUrl = baseExperience.type === 'demo' ? baseExperience.meetingUrl : null;
  const slug = useMemo(
    () => (meetingUrl ? extractMeetingSlug(meetingUrl) : null),
    [meetingUrl]
  );
  const timezone = useMemo(() => getUserTimezone(), []);

  // Probe `monthOffset=0` only — matches the booker's first calendar load so
  // queries dedupe, and a user is only blocked when *this* month has nothing.
  const { data, isLoading, isError } = useQueryMeetingAvailability(slug, 0, timezone);

  const probeStatus = useMemo<MeetingFallbackReason | null>(() => {
    if (baseExperience.type !== 'demo') return null;
    // A demo experience without a parseable slug means the configured meeting
    // URL is missing or malformed — the booker can't load anything, so treat
    // it as a fallback rather than rendering a dead calendar.
    if (!slug) return 'no_spots';
    if (isLoading) return null;
    if (isError) return 'upstream_error';
    if (!data) return null;
    if (data.success === false) return 'upstream_error';
    if ((data.data?.availabilities?.length ?? 0) === 0) return 'no_spots';
    return null;
  }, [baseExperience.type, slug, isLoading, isError, data]);

  // Dedupe analytics + logging: each (slug, reason) pair fires at most once
  // for the life of this hook instance. Guards against StrictMode
  // double-effects and the fallback flapping on rerenders.
  const firedReasonsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!probeStatus) return;
    // `slug` may be null when meetingUrl is malformed; key on `meetingUrl`
    // instead so the dedupe still works for that path.
    const key = `${slug ?? meetingUrl ?? 'unknown'}|${probeStatus}`;
    if (firedReasonsRef.current.has(key)) return;
    firedReasonsRef.current.add(key);
    logger.warn('Onboarding meeting fallback to request-demo', {
      reason: probeStatus,
      slug,
      country_code: args.countryCode,
      language: args.languageCode ?? undefined,
    });
    track({
      event_name: TrackingEventName.MeetingFallbackToRequestDemo,
      props: {
        reason: probeStatus,
        country_code: args.countryCode,
        language: args.languageCode ?? undefined,
      },
    });
  }, [probeStatus, slug, meetingUrl, args.countryCode, args.languageCode]);

  if (baseExperience.type !== 'demo') {
    return { experience: baseExperience, isProbing: false, fallbackReason: null };
  }

  if (probeStatus) {
    return {
      experience: { type: 'request-demo' },
      isProbing: false,
      fallbackReason: probeStatus,
    };
  }

  return {
    experience: baseExperience,
    isProbing: isLoading,
    fallbackReason: null,
  };
}
